import apiClient from './client';

export interface Plan {
  price: number;
  currency: string;
  popular?: boolean;
  features: string[]; // Feature codes, not human-readable text
}

export interface PlansResponse {
  free: Plan;
  standard: Plan;
  premium: Plan;
}

export interface PlansApiResponse {
  plans: PlansResponse;
  currentPlan: 'free' | 'standard' | 'premium';
}

const PLANS_CACHE_KEY = 'jubiel:subscriptionPlans:v2';

export function getCachedSubscriptionPlans(): PlansApiResponse | null {
  try {
    const raw = localStorage.getItem(PLANS_CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PlansApiResponse;
  } catch {
    return null;
  }
}

export function setCachedSubscriptionPlans(plans: PlansApiResponse): void {
  try {
    localStorage.setItem(PLANS_CACHE_KEY, JSON.stringify(plans));
  } catch {
    // ignore cache write failures (privacy modes, quota)
  }
}

/**
 * Get available subscription plans with pricing from the backend.
 * Returns only data (prices, currency, feature codes) - no human-readable text.
 * Frontend handles i18n translation.
 */
export async function getSubscriptionPlans(): Promise<PlansApiResponse> {
  const response = await apiClient.get<{ message_key?: string; data?: PlansApiResponse } | PlansApiResponse>('/subscriptions/plans');
  let data = response.data;
  
  // Handle wrapped response format { message_key, data }
  if (data && typeof data === 'object' && 'data' in data && data.data) {
    data = data.data;
  }
  
  // Handle both old format (direct plans) and new format (wrapped with currentPlan)
  if (data && typeof data === 'object' && 'plans' in data) {
    const parsed = data as PlansApiResponse;
    setCachedSubscriptionPlans(parsed);
    return parsed;
  }
  
  // Legacy format - wrap it
  const wrapped: PlansApiResponse = {
    plans: data as PlansResponse,
    currentPlan: 'free',
  };
  setCachedSubscriptionPlans(wrapped);
  return wrapped;
}

